import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CloseIcon, Text, UnsavedChangesDialog, useUnsavedChangesWarning } from '@viapeople/components'
import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { v7 as uuidv7 } from 'uuid'
import { getLogoUploadData } from '../../utils/files'
import { useToast } from '../../utils/ToastContext'
import { trpc } from '../../utils/trpc'
import { OrganizationForm } from './components/OrganizationForm'
import type { OrganizationFormInput } from './types'

const initialValues: OrganizationFormInput = {
	id: '',
	name: '',
	email: '',
	contactNumber: '',
	address: '',
	city: '',
	state: '',
	zipcode: '',
	country: '',
	logo: '',
	autoProvisionOktaUsers: false,
	defaultOktaPassword: null,
	defaultHierarchyFileId: null,
	loopModuleEnabled: false,
	enpsModuleEnabled: false,
	pecModuleEnabled: false,
	dealTeamModuleEnabled: false,
	caLimitedAccessEnabled: false,
}

export function CreateOrganizationPage() {
	const navigate = useNavigate()
	const toast = useToast()
	const [isLoading, setIsLoading] = useState(false)
	const queryClient = useQueryClient()
	const [organizationId] = useState(uuidv7())
	const [isDirty, setIsDirty] = useState(false)
	const { isBlocked, confirmLeave, cancelLeave } = useUnsavedChangesWarning(isDirty)

	const handleFormChange = useCallback((data: OrganizationFormInput, hasNewLogo?: boolean) => {
		const hasChanges = JSON.stringify(data) !== JSON.stringify(initialValues) || !!hasNewLogo
		setIsDirty(hasChanges)
	}, [])

	const { mutate: createOrganization } = useMutation(
		trpc.admin.organizations.create.mutationOptions({
			onSuccess: () => {
				setIsDirty(false) // Reset dirty state before navigation
				queryClient
					.invalidateQueries({
						queryKey: trpc.admin.organizations.search.queryKey(),
						exact: false,
						refetchType: 'all',
					})
					.then(() => {
						navigate('/organizations')
					})
				toast.showToast('Organization created')
			},
			onError: () => {
				toast.showToast('An error occurred. Try again later.', 'error')
			},
		}),
	)

	const { mutate: uploadLogoRequest } = useMutation(
		trpc.admin.organizations.logoUploadRequest.mutationOptions({
			onError: () => {
				toast.showToast('An error occurred. Try again later.', 'error')
			},
		}),
	)

	const onSubmit = async (data: OrganizationFormInput, logo?: File) => {
		if (!logo) {
			return
		}
		setIsLoading(true)

		const fileData = getLogoUploadData(organizationId, logo)

		uploadLogoRequest(fileData, {
			onSuccess: async (result) => {
				const response = await fetch(result.uploadUrl, {
					method: 'PUT',
					body: logo,
					headers: {
						'Content-Type': logo.type,
					},
				})
				if (response.ok) {
					createOrganization(
						{ ...data, id: organizationId, logo: result.readUrl ?? '' },
						{
							onSettled: () => setIsLoading(false),
						},
					)
				} else {
					toast.showToast('An error occurred. Try again later.', 'error')
					setIsLoading(false)
				}
			},
		})
	}

	const handleCancel = () => {
		navigate('/organizations')
	}

	const handleClose = () => {
		navigate('/organizations')
	}

	return (
		<main className='h-full'>
			<div className='flex justify-between items-center px-6 py-4 mb-6 border-b border-neutral-100'>
				<Text variant='medium' weight='semibold'>
					Create Organization
				</Text>
				<button type='button' onClick={handleClose} className='p-1 hover:bg-neutral-50 rounded transition-colors'>
					<CloseIcon />
				</button>
			</div>

			<OrganizationForm
				initialValues={initialValues}
				onSubmit={onSubmit}
				onCancel={handleCancel}
				onChange={handleFormChange}
				isSubmitting={isLoading}
				submitButtonText='Create Organization'
				cancelButtonText='Cancel'
			/>
			<UnsavedChangesDialog isBlocked={isBlocked} confirmLeave={confirmLeave} cancelLeave={cancelLeave} />
		</main>
	)
}
